/**
 * @file Stops `pointerdown` events from propagating to parent elements.
 * Works for all descendant elements of the bound element.
 *
 * Usage:
 *   <div v-stop-pointerdown-propagation>
 *     <button type="button">I won't propagate pointerdown</button>
 *   </div>
 */
import type { ObjectDirective } from 'vue'

// List of node names for which we want to block pointerdown propagation.
const BLOCKED_NODE_NAMES = new Set(['BUTTON', 'MEDIA-PLAY-BUTTON', 'MEDIA-MUTE-BUTTON'])
const BLOCKED_ELEMENTS_SELECTOR = Array.from(BLOCKED_NODE_NAMES).join(', ').toLowerCase()

interface ListenerTracker {
  node: HTMLElement
  handler: (event: PointerEvent) => void
}

interface StopPropagationElement extends HTMLElement {
  stopPointerdownPropagationObserver?: MutationObserver
  stopPointerdownPropagationListeners?: ListenerTracker[]
}

export const vPointerdownStop: ObjectDirective<StopPropagationElement> = {
  bind(el) {
    el.stopPointerdownPropagationListeners = []
  },

  inserted(el) {
    // A helper function that finds and attaches listeners on matching nodes
    // that do not already have them.
    function processSubtree(root: StopPropagationElement): void {
      // If we have no array to track listeners, initialize it
      if (el.stopPointerdownPropagationListeners == null) {
        el.stopPointerdownPropagationListeners = []
      }

      const targetElements = root.querySelectorAll<HTMLElement>(BLOCKED_ELEMENTS_SELECTOR)

      targetElements.forEach((node) => {
        // Double-check that the node actually has one of our blocked node names
        if (
          BLOCKED_NODE_NAMES.has(node.nodeName) &&
          el.stopPointerdownPropagationListeners?.some((tracker) => tracker.node === node) !== true
        ) {
          const handler = (event: PointerEvent): void => {
            event.stopPropagation()
          }

          node.addEventListener('pointerdown', handler)

          // Track it so we can remove it later
          el.stopPointerdownPropagationListeners?.push({
            node,
            handler,
          })
        }
      })
    }

    // Initial processing of the existing subtree
    processSubtree(el)

    // Set up a MutationObserver to watch for any added/removed nodes.
    const observer = new MutationObserver(() => {
      // For any changes, reprocess the entire subtree.
      // (We could optimize by only processing newly added nodes from the mutations,
      // but reprocessing is simpler and sufficient for many use-cases. And we already
      // have logic to skip nodes that are already processed.)
      processSubtree(el)
    })

    observer.observe(el, {
      childList: true,
      subtree: true,
    })

    // Store the observer so we can disconnect in unbind
    el.stopPointerdownPropagationObserver = observer
  },

  unbind(el) {
    // Disconnect the observer
    if (el.stopPointerdownPropagationObserver != null) {
      el.stopPointerdownPropagationObserver.disconnect()
      el.stopPointerdownPropagationObserver = undefined
    }

    // Remove all event listeners that we attached
    if (el.stopPointerdownPropagationListeners != null) {
      el.stopPointerdownPropagationListeners.forEach(({ node, handler }) => {
        node.removeEventListener('pointerdown', handler)
      })
      el.stopPointerdownPropagationListeners = []
    }
  },
}
